import { visibleControls } from "./controls";
import { normalizeVisibility } from "./resources";
import { CNAME_TARGET } from "./publishing";

// Converts between the getTrustCenter API shape and what the admin screens
// use, and builds each section's save input.

const MAX_INT = 2147483647;

// Ids made on the page for new rows (Date.now(), "new-1") are not database
// ids: send null so the server inserts them.
const savedId = (id) =>
  Number.isInteger(id) && id > 0 && id <= MAX_INT ? id : null;

const PUBLISH_DEFAULTS = {
  target: "regodit",
  customDomain: "",
  customDomainStatus: null,
  customDomainCheckedAt: null,
  customDomainStage: null,
  customDomainError: null,
  cnameTarget: CNAME_TARGET,
};

// API → screens. `publishing` holds choices made in Settings → Custom domain
// before the page is published (only stored when publishing).
export const toAdminView = (server, publishing = null) => {
  const { site } = server;
  return {
    ...server,
    integrationSubprocessors: Object.fromEntries(
      server.integrationSubprocessors.map(
        ({ key, name, purpose, isVisible, logoPath, dataLocations }) => [
          key,
          {
            name,
            purpose,
            isVisible,
            logoPath,
            dataLocations: dataLocations || [],
          },
        ]
      )
    ),
    resources: (server.resources || []).map((r) => ({
      ...r,
      visibility: normalizeVisibility(r.visibility),
    })),
    isPublished: site.isPublished,
    publishing: {
      ...PUBLISH_DEFAULTS,
      ...(site.isPublished || site.customDomain
        ? {
            target: site.publishTarget,
            customDomain: site.customDomain || "",
            customDomainStatus: site.customDomainStatus,
            customDomainCheckedAt: site.customDomainCheckedAt || null,
            customDomainStage: site.customDomainStage || null,
            customDomainError: site.customDomainError || null,
          }
        : {}),
      ...publishing,
      // From the API: what a customer's CNAME must point at.
      cnameTarget: site.cnameTarget || PUBLISH_DEFAULTS.cnameTarget,
    },
  };
};

// The /trust/<slug> link, before and after the first publish.
export const siteSlug = (site) => site?.slug || site?.suggestedSlug || "";

// One entry per save mutation, in the order they run. `keys` are the screen
// fields that belong to that section.
export const SECTIONS = [
  {
    id: "overview",
    keys: ["company"],
    variables: ({ company }) => ({
      input: {
        name: company.name || "",
        overview: company.overview || "",
        website: company.website || "",
        contactEmail: company.securityEmail || "",
        termsUrl: company.termsUrl || "",
        privacyUrl: company.privacyUrl || "",
        dataResidency: company.dataResidency || "",
        links: (company.customLinks || []).map((link) => ({
          id: savedId(link.id),
          label: link.label,
          url: link.url,
        })),
        // Hero banner image (null removes it).
        heroImagePath: company.heroImagePath || null,
        heroTitle: company.heroTitle || "",
        brandColor: company.brandColor || "",
        secondaryColor: company.secondaryColor || "",
        bannerStyle: company.bannerStyle || null,
        bannerStartColor: company.bannerStartColor || "",
        bannerEndColor: company.bannerEndColor || "",
        bannerTextColor: company.bannerTextColor || null,
        fontFamily: company.fontFamily || null,
        customFontName: company.customFontName || "",
        customFontFiles: (company.customFontFiles || []).map(
          ({ path, weight, style }) => ({ path, weight, style })
        ),
        faviconPath: company.faviconPath || null,
      },
    }),
  },
  {
    id: "access",
    keys: ["access", "nda"],
    variables: ({ access, nda }) => ({
      input: {
        mode: access.mode,
        days: Number(access.days),
        allowedDomains: access.allowedDomains,
        invitedEmails: access.invitedEmails.map((entry) =>
          typeof entry === "string" ? entry : entry.email
        ),
        ndaRequired: !!nda.required,
        ndaText: nda.text || "",
      },
    }),
  },
  {
    id: "categories",
    keys: ["resourceCategories"],
    variables: ({ resourceCategories }) => ({
      categories: resourceCategories.map((c) => ({
        id: savedId(c.id),
        name: c.name,
        description: c.description || "",
      })),
    }),
  },
  {
    id: "resources",
    keys: ["resources"],
    variables: ({ resources }) => ({
      resources: resources.map((r) => ({
        id: savedId(r.id),
        categoryId: r.categoryId,
        type: r.type,
        name: r.name,
        description: r.description || "",
        visibility: normalizeVisibility(r.visibility),
        policyId: r.policyId ?? null,
        documentId: r.documentId ?? null,
        url: r.url ?? null,
      })),
    }),
  },
  {
    id: "frameworks",
    keys: ["frameworks"],
    variables: ({ frameworks }) => ({
      frameworks: frameworks.map((f) => ({
        id: f.id,
        isVisible: !!f.isVisible,
        status: f.status,
        description: f.description || "",
        validUntil: f.validUntil || "",
        resourceIds: (f.resourceIds || []).filter((id) => savedId(id)),
      })),
    }),
  },
  {
    id: "subprocessors",
    keys: ["subprocessors", "integrationSubprocessors"],
    variables: ({ subprocessors, integrationSubprocessors }) => ({
      input: {
        manual: subprocessors.map((s) => ({
          id: savedId(s.id),
          name: s.name,
          purpose: s.purpose,
          isVisible: s.isVisible === true,
          dataLocations: s.dataLocations || [],
          logoPath: s.logoPath || null,
        })),
        integrations: Object.entries(integrationSubprocessors || {}).map(
          ([key, s]) => ({
            key,
            purpose: s.purpose || "",
            isVisible: !!s.isVisible,
            dataLocations: s.dataLocations || [],
          })
        ),
      },
    }),
  },
  {
    id: "faqs",
    keys: ["faqs"],
    variables: ({ faqs }) => ({
      faqs: faqs.map((f) => ({
        id: savedId(f.id),
        question: f.question,
        answer: f.answer,
      })),
    }),
  },
  {
    id: "updates",
    keys: ["updates"],
    variables: ({ updates }) => ({
      updates: updates.map((u) => ({
        id: savedId(u.id),
        title: u.title,
        category: (u.category || "").trim(),
        body: u.body,
        status: u.status,
      })),
    }),
  },
  {
    // Who may accept / decline access requests (a setting, not page
    // content: no republish needed).
    id: "approvers",
    keys: ["approverEmails"],
    variables: ({ approverEmails }) => ({ emails: approverEmails || [] }),
  },
  {
    // Which controls visitors see; the controls themselves are not edited.
    id: "controls",
    keys: ["controlVisibility", "controlsShown", "hiddenControlIds"],
    variables: ({ controlVisibility, controlsShown, hiddenControlIds }) => ({
      visibility: controlVisibility,
      shown: controlsShown === true,
      hiddenControlIds: hiddenControlIds || [],
    }),
  },
];

export const sectionsFor = (changes) =>
  SECTIONS.filter((section) => section.keys.some((key) => key in changes));

// What a visitor would see, built from the admin data (for Preview, before
// the page is published). Mirrors the public API: no private items, hidden
// frameworks or drafts. Lists show public and requestable resources.
export const toPublicView = (admin) => {
  const resources = admin.resources.filter((r) => r.visibility !== "private");
  const listed = new Set(resources.map((r) => r.id));
  return {
    ...admin,
    isPublished: admin.site.isPublished,
    updatedAt: new Date().toISOString(),
    resources,
    resourceCategories: admin.resourceCategories.filter((c) =>
      resources.some((r) => r.categoryId === c.id)
    ),
    frameworks: admin.frameworks
      .filter((f) => f.isVisible)
      .map((f) => ({
        ...f,
        resourceIds: f.resourceIds.filter((id) => listed.has(id)),
      })),
    updates: admin.updates.filter((u) => u.status === "published"),
    controls: visibleControls(
      admin.controls,
      admin.controlVisibility,
      admin.frameworks,
      admin
    ),
    subprocessors: [
      ...admin.integrationSubprocessors
        .filter((s) => s.isVisible && s.connected)
        .map((s) => ({
          id: `integration-${s.key}`,
          name: s.name,
          purpose: s.purpose,
          logoPath: s.logoPath || null,
          dataLocations: s.dataLocations || [],
        })),
      ...admin.subprocessors
        .filter((s) => s.isVisible)
        .map((s) => ({
          id: `manual-${s.id}`,
          name: s.name,
          purpose: s.purpose,
          logoPath: s.logoPath || null,
          dataLocations: s.dataLocations || [],
        })),
    ].sort((a, b) => a.name.localeCompare(b.name)),
  };
};
